/**
 * Shows why the balance sheet does not reconcile.
 *
 * The findings are deterministic and come back instantly, so they are fetched
 * on mount rather than behind a button — a user looking at an imbalance should
 * not have to ask what caused it. The AI write-up costs money and takes
 * seconds, so that one is behind a button.
 *
 * Origin findings are separated from their consequences. A wrong closing
 * balance breaks the next period's roll-forward too, and listing all of them
 * as equals sends the user correcting figures that are downstream of the real
 * error.
 */

import { useEffect } from 'react';
import { Markdown } from '../Markdown';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type { BalanceFinding } from '../../../types/financial-model-v2';

function money(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function FindingRow({ finding }: { finding: BalanceFinding }) {
  return (
    <div className="px-4 py-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-gray-900">
          {finding.period.replace('_', ' ')}
        </span>
        <span className="tabular-nums text-sm font-medium text-red-700">
          out by {money(finding.discrepancy)}
        </span>
      </div>
      <div className="mt-0.5 text-xs tabular-nums text-gray-500">
        expected {money(finding.expected)} · got {money(finding.actual)}
      </div>
    </div>
  );
}

export function BalanceDiagnosisPanel() {
  const model = useFinancialModelV2Store((s) => s.model);
  const diagnosis = useFinancialModelV2Store((s) => s.diagnosis);
  const diagnoseBalance = useFinancialModelV2Store((s) => s.diagnoseBalance);
  const analyseBalance = useFinancialModelV2Store((s) => s.analyseBalance);
  const isAnalysing = useFinancialModelV2Store((s) => s.isAnalysingBalance);

  // Re-run whenever the model changes: a stale diagnosis describing figures
  // the user has since corrected is worse than none.
  useEffect(() => {
    void diagnoseBalance();
  }, [diagnoseBalance, model?.version]);

  if (!model) return null;

  if (!diagnosis) {
    return (
      <div className="rounded border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">
        Checking the model…
      </div>
    );
  }

  const healthy = diagnosis.balances && diagnosis.findings.length === 0;
  if (healthy) {
    return (
      <div className="rounded border border-green-300 bg-green-50 px-4 py-2 text-sm text-green-900">
        {diagnosis.summary}
      </div>
    );
  }

  // A sheet that balances while its checks fail is the offsetting-errors case:
  // serious, but not the same as an imbalance, and coloured differently so the
  // two do not read as one problem.
  const tone = diagnosis.balances
    ? 'border-amber-300 bg-amber-50 text-amber-900'
    : 'border-red-300 bg-red-50 text-red-900';

  const byCheck = new Map<string, BalanceFinding[]>();
  for (const finding of diagnosis.findings) {
    const group = byCheck.get(finding.check) ?? [];
    group.push(finding);
    byCheck.set(finding.check, group);
  }

  return (
    <div className="space-y-3">
      <div className={`rounded border px-4 py-3 text-sm ${tone}`}>
        {diagnosis.summary}
      </div>

      {[...byCheck.entries()].map(([check, findings]) => {
        const origin = findings.find((f) => f.isOrigin);
        const consequences = findings.filter((f) => !f.isOrigin);
        return (
          <div
            key={check}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white"
          >
            <div className="border-b border-gray-200 px-4 py-2.5">
              <div className="text-sm font-semibold capitalize text-gray-900">
                {check}
              </div>
              <p className="mt-1 text-xs text-gray-600">
                {findings[0].explanation}
              </p>
            </div>
            {origin && (
              <div className="border-b border-gray-100 bg-red-50/40">
                <div className="px-4 pt-2 text-[10px] uppercase tracking-wide text-red-700">
                  Start here — first period this fails
                </div>
                <FindingRow finding={origin} />
              </div>
            )}
            {consequences.length > 0 && (
              <div>
                <div className="px-4 pt-2 text-[10px] uppercase tracking-wide text-gray-400">
                  Knock-on effects of the above
                </div>
                <div className="divide-y divide-gray-50">
                  {consequences.map((finding) => (
                    <FindingRow key={finding.period} finding={finding} />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {!diagnosis.fullyExplained && (
        <div className="rounded border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          Some periods are out of balance without a failing check. Look at the
          lines the checks do not cover: opening balances, investments, and any
          control account closing actual that overrides a computed figure.
        </div>
      )}


      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
          <span className="text-sm font-semibold text-gray-700">
            AI explanation
          </span>
          <button
            type="button"
            disabled={isAnalysing}
            onClick={() => void analyseBalance()}
            className="rounded border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            {isAnalysing
              ? 'Analysing…'
              : model.aiBalanceAnalysis
                ? 'Regenerate'
                : 'Explain these findings'}
          </button>
        </div>
        <div className="px-4 py-3 text-sm text-gray-800">
          {model.aiBalanceAnalysis ? (
            <Markdown>{model.aiBalanceAnalysis}</Markdown>
          ) : (
            <span className="text-gray-500">
              The findings above are computed, not guessed — they stand on
              their own. Ask for an explanation to get the likely cause and a
              prescribed fix.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
